import React from 'react'
import PropTypes from 'prop-types'
import isEmpty from 'lodash/isEmpty'
import { FormattedMessage } from 'react-intl'

// Helpers
import { openLink } from '../../../../api/iframe/requests'

// Constants
import { SIGN_UP_STEPS } from '../../../../constants/signUp'
import { TOS_LINK, PRIVACY_LINK } from '../../../../constants/navigation'

// Components
import { Button, If, Unless, Text } from '../../../../Components'

// Providers
import { useWizardProvider } from '../../../../Components/shared/WizardContext'
import Step from '../../../../Components/shared/WizardContext/Step'
import { useSignUpProvider } from '../SignUpContext'

// Step Components
import Name from './Name'
import Email from './Email'
import MagicLink from './MagicLink'
import ClientNotFound from './ClientNotFound'
import SignUpError from '../Error'

// Styles
import styles from './Steps.module.scss'

const Steps = ({ handleClose }) => {
  const {
    formState,
    iFramed,
    handleIframeClose,
    handleCheckClient,
    handleCreateClick,
    handleResetClick,
  } = useSignUpProvider()
  const { currentStep } = useWizardProvider()

  const linkProps = link => {
    if (iFramed) {
      return {
        onClick: () => openLink(link),
      }
    }
    return {
      target: '_blank',
      rel: 'noopener noreferrer',
      href: link,
    }
  }

  return (
    <div className={styles.container}>
      <section className={styles.contentBody}>
        <SignUpError />
        <Step step={SIGN_UP_STEPS.USER_EMAIL}>
          <Email />
        </Step>
        <Step step={SIGN_UP_STEPS.USER_FIRST_AND_LAST}>
          <Name />
        </Step>
        <Step step={SIGN_UP_STEPS.COMPLETE}>
          <MagicLink className={styles.step} />
        </Step>
        <Step step={SIGN_UP_STEPS.CLIENT_NOT_FOUND}>
          <ClientNotFound className={styles.step} />
        </Step>
      </section>
      <footer className={styles.buttonContainer}>
        <If condition={currentStep === SIGN_UP_STEPS.USER_EMAIL}>
          <Button
            disabled={isEmpty(formState.email)}
            className={styles.continueBtn}
            fullWidth
            primary
            onClick={handleCheckClient}
          >
            <FormattedMessage
              id='NewBuyerSignUp.emailStep.continueBtn'
              defaultMessage='Continue'
            />
          </Button>
        </If>
        <If condition={currentStep === SIGN_UP_STEPS.USER_FIRST_AND_LAST}>
          <Button
            disabled={
              isEmpty(formState.firstName) || isEmpty(formState.lastName)
            }
            className={styles.continueBtn}
            fullWidth
            primary
            onClick={handleCreateClick}
          >
            <FormattedMessage
              id='NewBuyerSignUp.nameStep.createBtn'
              defaultMessage='Create an account'
            />
          </Button>
        </If>

        <If condition={currentStep === SIGN_UP_STEPS.CLIENT_NOT_FOUND}>
          <Button
            fullWidth
            primary
            className={styles.continueBtn}
            onClick={handleResetClick}
          >
            <FormattedMessage
              id='NewBuyerSignUp.clientNotFound.diffEmail'
              defaultMessage='Try a different email'
            />
          </Button>
        </If>
        <Unless
          condition={
            currentStep === SIGN_UP_STEPS.COMPLETE || (!iFramed && !handleClose)
          }
        >
          <Button minimal danger onClick={handleClose || handleIframeClose}>
            <FormattedMessage
              id='NewBuyerSignUp.nameStep.cancelBtn'
              defaultMessage='Cancel'
            />
          </Button>
        </Unless>
        <If condition={currentStep === SIGN_UP_STEPS.USER_FIRST_AND_LAST}>
          <div className={styles.disclaimerContainer}>
            <Text
              align='center'
              variant='neutralCool'
              variantCount='600'
              size='s'
            >
              <FormattedMessage
                id='NewBuyerSignUp.nameStep.disclaimer'
                defaultMessage="By signing up, you're agreeing to our {termsOfUse} and {privacy}"
                values={{
                  termsOfUse: (
                    <Button
                      minimal
                      className={styles.disclaimer}
                      element='a'
                      {...linkProps(TOS_LINK)}
                    >
                      <FormattedMessage
                        id='NewBuyerSignUp.nameStep.termsOfUse'
                        defaultMessage='Terms of Use'
                      />
                    </Button>
                  ),
                  privacy: (
                    <Button
                      minimal
                      className={styles.disclaimer}
                      element='a'
                      {...linkProps(PRIVACY_LINK)}
                    >
                      <FormattedMessage
                        id='NewBuyerSignUp.nameStep.privacy'
                        defaultMessage='Privacy'
                      />{' '}
                    </Button>
                  ),
                }}
              />
            </Text>
          </div>
        </If>
      </footer>
    </div>
  )
}

Steps.propTypes = {
  handleClose: PropTypes.func,
}

export default Steps
